import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject, defer, forkJoin, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, shareReplay, switchMap, take, timeout } from 'rxjs/operators';
import * as _ from 'lodash';
import { Loads, SetInsertService, DraftSummaryResponse } from './set-insert.service';
import {
  DraftFeature, SET_INSERT_FEATURE, featureForKey, resolveActiveFeature as registryResolve,
} from './draft-features';
import { readStorage, removeStorage, writeStorage } from 'src/app/shared/utils/safe-storage';
import { TAB_ID_STORAGE_KEY, generateTabId, getTabId, peekTabId, setTabId } from './tab-id';

export const SI_DRAFT_PREFIX = SET_INSERT_FEATURE.prefix; // 'si:'
// Generic per-tab id so other features can reuse it (browser sessionStorage is
// already per-tab, so a single value is enough to identify this tab everywhere).
// The id itself lives in `tab-id.ts` -- this service owns only the duplicate-tab
// claim below. Re-exported so existing importers keep working.
export { TAB_ID_STORAGE_KEY };
const TAB_ID_CHANNEL = 'tabId-coordinator';
const TAB_ID_CLAIM_TIMEOUT_MS = 500;
// One-shot flag set on saveDefinitive success + consumed by the next conf mount
// to SKIP the draft picker entirely. Defends the post-commit transition
// (/set-insert-out -> /set-insert-out/:id) from offering any picker (own draft
// OR other drafts saved in this tab) at a moment the user just acted decisively
// on their work. Drafts remain visible on direct /:id visits; the flag is
// per-tab (sessionStorage) and gets cleared by the very next conf mount.
// EXPORTED for the spec backstop alone (`draft-flag-test-hooks.ts`), which clears it after every
// spec: it is REAL sessionStorage shared by the whole Karma page, and a spec that leaves it set
// makes the next mount of a conf component skip the branch that subscribes to the suppression
// channel. Imported there rather than spelled as a literal, so renaming this key cannot leave the
// backstop silently pointing at nothing. Nothing in the app may read or write it directly --
// `markJustCommitted`/`consumeJustCommitted` below are the only access this flag has.
export const JUST_COMMITTED_KEY = 'tabId.justCommitted';
// How long a draft write's OWN request may stay on the wire before giving up. A write whose XHR
// hangs without erroring (stalled proxy, wedged backend thread — XHR has no default timeout)
// would otherwise block silently and forever: the restore picker never offers this tab's draft
// again, and — worse — no LATER draft write would ever be issued, so the request that could have
// healed the backend draft is dropped too. The deadline is armed when the REQUEST starts, never
// at enqueue (`chainDraftWrite`): an enqueue-anchored clock measured queue-wait, so under one
// genuinely slow head-of-line write every queued successor's timer fired against a prior whose
// own request was seconds old and healthy — serially aborting writes that would have landed.
// (The listing gate carries NO clock of its own for the same reason: every outcome it can wait
// on is already bounded by this per-request deadline, and a second, enqueue-anchored clock on
// the gate latched settles as failed for merely QUEUEING behind a slow prior — hiding this
// tab's draft, the only copy of the remaining rows, on a rewrite that actually landed.) Past
// the deadline the hung write's outcome is unknown, so the waiters fail safe: the listing hides
// this tab's own draft for that mount, and every write still in the same unbroken chain reports
// failure even when its own request succeeded (the zombie prior may still land on the backend
// AFTER any of them, restoring the stale draft — see `taintedSlots`).
export const SETTLE_LIST_TIMEOUT_MS = 10000;

// How long the draft LISTING's own GET may stay on the wire. Same argument as the write deadline
// above, applied to the one request in `listPendingDrafts` that had none: every write it waits out
// self-bounds, so the gate needs no clock — but the GET that follows the gate is a plain
// `HttpClient` call, and XHR has no default timeout. On the stalled-backend case the write
// deadlines exist for (a proxy that accepts the socket and never answers) the mounting screen was
// left under `LayoutActionType.LoadingBlock` — a full-screen modal overlay — with
// `hasPendingDrafts` stuck true, forever, curable only by a reload. This is a PER-REQUEST bound,
// not the whole-gate clock rejected in review round 15: it measures one request's own time on the
// wire and cannot mistake a healthy queue for a wedged one. On firing, the listing errors like any
// other failed GET, and both consumers already release the gate and lower the block in their
// `catchError`/`finalize`.
export const LIST_DRAFTS_TIMEOUT_MS = 10000;

export interface PendingDraft {
  key: string;
  savedAt: string | null;
  itemCount: number;
  brand?: string;
  value: Loads;
}

/** How one write in a feature's draft-write chain ended. `hung` is a write whose own request
 *  outlived its deadline and was torn down mid-flight (`chainDraftWrite`) — distinct from a clean
 *  `failed`, because only a hung write can still LAND on the backend afterwards (a request the
 *  server received in full can be applied after the socket drops). A hung write TAINTS its slot
 *  (`taintedSlots`): every write in the same unbroken chain reports `failed` even when its own
 *  request succeeds, because the zombie can land after ANY of them — clamping only the immediate
 *  successor let the write two positions behind report `landed` while the stale draft could still
 *  be resurrected after it. */
type ChainOutcome = 'ok' | 'failed' | 'hung';

/** What KIND of write a chain entry is — the property that decides whether a fresher write of the
 *  same slot may SUPERSEDE it while it is still queued. `autosave` and `settle-rewrite` are both
 *  full-grid snapshots aimed at this tab's own key, so within each kind only the newest enqueuer's
 *  payload is worth sending; `settle-delete` (post-commit deletes and the picker's per-draft
 *  delete) is never superseded and never supersedes — a user-initiated delete must not have its
 *  request swapped for a payload that re-creates the draft, and nothing may swap ITS request out
 *  either. */
type ChainWriteKind = 'autosave' | 'settle-rewrite' | 'settle-delete';

/** One write in a feature's draft-write chain: its replayed tri-state OUTCOME, what KIND of write
 *  it is, and — while it has neither fired nor finished — the ability to be SUPERSEDED. A write
 *  queued behind a slow prior is (for the coalescible kinds) a full-grid snapshot that only the
 *  newest enqueuer cares about (every one of a feature overwrites the same key), so a newer write
 *  of the same kind replaces the queued one's payload instead of queueing behind it: without
 *  that, a backlog behind one slow write POSTed every dead-on-arrival snapshot serially — for
 *  per-row saves, N full-draft rewrites of the SAME key, quadratic bytes on a slow connection —
 *  and pinned each payload in memory for the wait. A superseded write's waiters (including a
 *  listing gate) share the surviving write's outcome: the entry is reused, so its replayed
 *  subject is the same object. */
interface ChainedDraftWrite {
  outcome: Observable<ChainOutcome>;
  kind: ChainWriteKind;
  /** Replace the not-yet-started write's request with a fresher one. Returns whether the swap
   *  happened — `false` once the request is on the wire (or done), in which case the caller must
   *  enqueue normally. */
  supersede(request: Observable<unknown>): boolean;
  /** The boolean view callers get: `true` exactly when the write landed with the chain intact. */
  landed: Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class SetInsertDraftService {

  private _hasPendingDrafts = false;
  /** How many commits are in flight, NOT whether one is — see `isSaving`. */
  private _savesInFlight = 0;
  private _pendingRestore: PendingDraft | null = null;
  private claimChannel: BroadcastChannel | null = null;
  /** The post-commit draft rewrite/delete of each FEATURE, keyed by the feature's prefix — see
   *  `settleAfterCommit`. Emits whether the settle SUCCEEDED: `listPendingDrafts` hides THIS
   *  TAB'S OWN draft on `false`, because a failed rewrite leaves the PRE-commit draft on the
   *  backend under this tab's key and listing it offers a restore that re-commits
   *  already-committed rows. Other tabs' drafts stay listed — this tab's failed rewrite says
   *  nothing about their backend state, and hiding them sent the operator back to redo work a
   *  perfectly good draft still held.
   *
   *  Keyed per feature because the listing is: only the set-insert grid settles today, and gating
   *  the TRANSFER/UNLOAD pickers on a set-insert settle suppressed drafts whose backend state a
   *  set-insert failure says nothing about — the operator, told "no draft", redid the work from
   *  scratch and the next autosave overwrote the real draft with the redone partial state. The
   *  same wrong-scope lesson is why the suppression is now also keyed to the tab's own draft.
   *
   *  A slot holding a settle that REPORTED FAILURE is kept (see `settleAfterCommit`), so it keeps
   *  replaying `false` — instantly, via shareReplay — to every later listing until a later settle
   *  succeeds and clears it, a chained autosave lands the full current state and heals it (see
   *  `saveDraftForFeature`), or the next listing RETRIES the settle itself and it lands
   *  (`retryLatchedSettle` — the only healer available on a screen the post-commit navigation has
   *  already emptied). */
  private settleInFlight = new Map<string, Observable<boolean>>();

  /** Fires when a `listPendingDrafts` actually HID this tab's own draft because the feature's
   *  settle reported failure — i.e. the backend row exists and was deliberately not offered.
   *
   *  A plain `Subject`, not a state flag: the consumer that mounted the listing is the one that has
   *  to explain it, and a listing that suppressed nothing must say nothing. The service raises no
   *  toast itself — it has no UI dependency, and two different screens (the set-insert conf and the
   *  transfer/unload filter) list drafts with their own snackbars. */
  private readonly ownDraftSuppressed = new Subject<DraftFeature>();

  /** See `ownDraftSuppressed`. */
  get ownDraftSuppressed$(): Observable<DraftFeature> { return this.ownDraftSuppressed.asObservable(); }

  /** Settles that have COMPLETED with failure — the latched entries of `settleInFlight`, as
   *  opposed to ones still in flight. A successful chained autosave may clear a latched settle
   *  (the backend draft now holds the full current state) but must never touch an in-flight one:
   *  an autosave that raced a commit could otherwise unlatch the very settle whose outcome the
   *  next listing needs to wait for. WeakSet — a replaced latch is unreachable once the slot
   *  moves on, and nothing ever iterates it. */
  private settledFailed = new WeakSet<Observable<boolean>>();

  /** The request behind each feature's LATCHED settle, kept so the next listing can RE-RUN it —
   *  see `retryLatchedSettle`. Dropped as soon as the slot's settle succeeds or is replaced. Every
   *  settle request is built inside a `defer` (`settleRewrite`, `deleteToleratingMissing`), so
   *  re-subscribing issues a fresh round-trip rather than replaying a dead one. */
  private latchedSettleRequest = new Map<string, {
    request: Observable<unknown>;
    kind: Exclude<ChainWriteKind, 'autosave'>;
    /** `slotWriteLanded` for this slot at the moment the latch was taken — the freshness stamp the
     *  retry checks itself against. See `retryLatchedSettle`. */
    landedSeq: number;
  }>();

  /** How many writes aimed at THIS TAB's own draft key have LANDED on each feature slot — i.e.
   *  completed with their own request succeeding, before any `taintedSlots` clamp. Not an outcome
   *  count: a write clamped to `failed` by a hung sibling still put its payload on the backend, and
   *  that is exactly what this has to record. Its only reader is `retryLatchedSettle`, which must
   *  not re-fire a commit-time snapshot over a fresher payload that has since landed.
   *
   *  The own-key restriction is what keeps the count meaning what the retry reads it as. The whole
   *  slot shares one write chain — the picker's per-draft delete rides the chain of the feature its
   *  key belongs to, ANY tab's key — so counting every landed write let a delete of some OTHER
   *  tab's draft answer "has a fresher payload landed on OUR key?" with yes. The retry was then
   *  dropped for good and this tab's draft, the only copy of the rows the commit did not carry,
   *  stayed hidden from every picker for the life of the page while `ownDraftUnavailable` kept
   *  firing — the exact state the retry exists to end. */
  private slotWriteLanded = new Map<string, number>();

  /** The LAST draft write of each feature — settle or autosave — that a new write must chain
   *  behind. Distinct from `settleInFlight`, which only ever holds settles (the listing gate):
   *  ordering is a property of every write. Serializing only settles against each other left the
   *  autosave loop free-running, and a settle delayed behind a slow prior could land AFTER a
   *  fresher autosave and overwrite the backend draft with its older payload snapshot — work the
   *  operator typed after the commit, silently gone on the next restore. Payloads are snapshotted
   *  at enqueue time on every path (a coalesced autosave's payload is REPLACED at supersede time,
   *  which is that fresher snapshot's own enqueue), so enqueue order IS freshness order, and FIFO
   *  delivery keeps the freshest write last. Entries are dropped on completion; every write's own
   *  request is bounded and self-aborted past `SETTLE_LIST_TIMEOUT_MS` — see `chainDraftWrite`. */
  private draftWriteTail = new Map<string, ChainedDraftWrite>();

  /** Feature slots whose current, UNBROKEN write chain contains a write that HUNG — was torn down
   *  past its deadline with its outcome unknown. While the taint stands, every completing write of
   *  the slot reports `failed` even when its own request succeeded: the hung request may have been
   *  received in full by the server, so it can be applied AFTER any number of later round-trips,
   *  restoring the stale draft behind them. Clamping only the write chained immediately behind the
   *  hung one (the old rule) let the write two positions back report `landed` while that hazard
   *  still stood. The taint clears when the chain DRAINS (the slot's tail completes and is
   *  removed): a fresh write enqueued onto an idle slot starts clean, which is the same reach the
   *  old single-link clamp had for a lone hung write — nothing client-side can bound the server's
   *  apply, so the chain is the honest scope.
   *
   *  It clamps only writes aimed at THIS TAB's key (`targetsOwnKey`): the hazard is a zombie
   *  RE-CREATING a draft, and only the autosave and the settle rewrite write one — both to
   *  `tabKeyFor(feature)`. A picker delete of another tab's key shares the slot's chain and
   *  nothing in it can undo that delete, so clamping it only ever reported a landed delete as
   *  failed. */
  private taintedSlots = new Set<string>();

  /** The picker's chained per-draft deletes still in flight (or queued), keyed by the DRAFT KEY
   *  they target. `listPendingDrafts` waits these out for its feature the same way it waits out a
   *  settle: an unregistered queued delete let the listing's GET beat the DELETE to the backend
   *  and re-offer — for restore — the very draft the user just deleted. Each entry's outcome is
   *  bounded (every chained write self-bounds), so the wait is too; entries drop on completion,
   *  after which the backend answers for itself (a landed delete is simply no longer listed, a
   *  failed one still exists and is listed truthfully). */
  private pendingDeletes = new Map<string, Observable<boolean>>();

  constructor(private setInsertService: SetInsertService) {
    // Browser "Duplicate Tab" copies sessionStorage to the new tab - both
    // would otherwise share the same tabId and their autosaves would clobber
    // each other's drafts. On service init, ping the channel for whoever is
    // already using our tabId; if anyone answers, regenerate. Also stay
    // listening so future duplicates we observe get told we're here.
    this.claimTabId();
  }

  private claimTabId(): void {
    if (typeof BroadcastChannel === 'undefined') { return; }
    // Migrate the previous storage key (used while the feature was still
    // namespaced under si.*) so existing tabs keep their draft binding.
    const legacy = readStorage('session', 'si.tabId');
    if (legacy && !peekTabId()) {
      // Adopt through setTabId, which keeps the id in memory when storage refuses the write, and
      // only drop the legacy key once the new one is DURABLE. Dropping it on a refused write would
      // leave the tab with no id after the next reload and scatter its existing drafts -- and that
      // is the likely case, since a quota that refuses `setItem` still honours `removeItem`
      // (removing is what frees room), so the migration would delete without having copied.
      if (setTabId(legacy)) {
        removeStorage('session', 'si.tabId');
      }
    }
    this.claimChannel = new BroadcastChannel(TAB_ID_CHANNEL);
    this.claimChannel.addEventListener('message', (e: MessageEvent) => {
      // peekTabId, not a storage read: a tab whose id only lives in memory (storage refused the
      // write) must still answer pings for it, or a later duplicate would think the id is free.
      // It must also NOT mint one -- answering a ping is not a reason for this tab to claim an id.
      const current = peekTabId();
      if (e.data && e.data.type === 'ping' && current && current === e.data.tabId) {
        this.claimChannel?.postMessage({ type: 'pong', tabId: current });
      }
    });
    const existing = peekTabId();
    if (!existing) { return; } // nothing to claim yet; tabKey getter will mint one
    const myTabId = existing;
    const onPong = (e: MessageEvent) => {
      if (e.data && e.data.type === 'pong' && e.data.tabId === myTabId) {
        // Another tab is already using our tabId - we are a duplicate. Mint a
        // fresh one so our autosave writes to its own draft slot.
        const fresh = generateTabId();
        setTabId(fresh);
        this.claimChannel?.removeEventListener('message', onPong);
      }
    };
    this.claimChannel.addEventListener('message', onPong);
    this.claimChannel.postMessage({ type: 'ping', tabId: myTabId });
    setTimeout(() => this.claimChannel?.removeEventListener('message', onPong), TAB_ID_CLAIM_TIMEOUT_MS);
  }

  /**
   * A draft the user picked Restore on - stashed by SetInsertConfComponent
   * (which owns the picker) for TableEditRowsComponent to consume once it
   * mounts. The conf dispatches the draft's filter to make the table mount;
   * the table sees the pending restore on its first dataSource emission and
   * overwrites the just-loaded server rows with the draft's items.
   */
  get pendingRestore(): PendingDraft | null { return this._pendingRestore; }
  setPendingRestore(draft: PendingDraft | null): void { this._pendingRestore = draft; }
  consumePendingRestore(): PendingDraft | null {
    const d = this._pendingRestore;
    this._pendingRestore = null;
    return d;
  }

  /**
   * True while the list endpoint reports the user has unresolved pending drafts.
   * Set as soon as we know drafts exist (BEFORE the picker is rendered, so
   * Search/autosave fired during the listPendingDrafts HTTP window are also blocked),
   * cleared once the user resolves them or the list comes back empty. Consumers
   * (autosave loop, Search button, search-as-you-type header, dataSource subscriber)
   * gate themselves on this so a pending-draft decision is never raced.
   */
  get hasPendingDrafts(): boolean { return this._hasPendingDrafts; }
  setHasPendingDrafts(has: boolean): void { this._hasPendingDrafts = has; }

  /**
   * True while ANY saveDefinitive HTTP is in flight. Gates the autosave loop so
   * a periodic saveDraft can't race with the commit (specifically: can't land
   * AFTER the post-commit deleteDraft and resurrect the row, or capture
   * mid-reset() state and persist empty-value rows). Set by TableEditRowsComponent
   * at the top of saveDefinitive and cleared via finalize() so it always
   * releases - on success, error, or component-destroy unsubscribe.
   *
   * Counted, not a boolean, because commits genuinely OVERLAP: a per-row save of a row the bulk
   * never snapshotted stays live for the whole bulk round trip by design (`isRowBulkLocked`), and
   * each save's own `finalize` clears independently. A boolean let whichever finished FIRST
   * re-open the autosave loop while the other was still on the wire — so a tick could snapshot
   * mid-commit state and, landing after the settle had armed its gate, heal the failed-settle
   * latch with a payload still describing committed rows. Only the LAST commit to finish releases
   * the gate. Never goes below zero: an unbalanced release (a spec, a future caller clearing
   * twice) must not leave the counter negative and the gate permanently shut.
   */
  get isSaving(): boolean { return this._savesInFlight > 0; }
  setIsSaving(saving: boolean): void {
    this._savesInFlight = saving ? this._savesInFlight + 1 : Math.max(0, this._savesInFlight - 1);
  }

  /** This tab's id, minted on first use. Shared with every other per-tab consumer (see `tab-id`). */
  private get tabId(): string {
    return getTabId();
  }

  get tabKey(): string {
    return this.tabKeyFor(SET_INSERT_FEATURE);
  }

  tabKeyFor(feature: DraftFeature): string {
    return feature.prefix + this.tabId;
  }

  resolveActiveFeature(url: string): DraftFeature | null {
    return registryResolve(url);
  }

  /**
   * Persists a draft snapshot — an AUTOSAVE — through the per-feature write chain, so it can
   * neither overtake a settle already enqueued (whose payload is older) nor be overtaken by one
   * enqueued later (whose payload is newer). The HTTP is issued by the chain itself the moment the
   * prior write completes, whether or not the caller subscribes; the returned observable replays
   * the write's OUTCOME (`true` = landed with the chain intact) and never errors.
   *
   * A successful autosave also HEALS a latched failed settle of the same feature: its payload is
   * the full current grid state, so once it lands the backend draft no longer describes the
   * pre-commit rows the latch was protecting the listing from. Only a COMPLETED-failed settle is
   * cleared (`settledFailed`) — an in-flight one must keep gating the listing until its own
   * outcome is known — and a `false` outcome (own failure, or a hung prior that may still land
   * afterwards) proves nothing and heals nothing.
   */
  saveDraftForFeature(feature: DraftFeature, loads: Loads): Observable<boolean> {
    const slot = feature.prefix;
    // Snapshot at ENQUEUE, here at the chain's single choke point. The chain's ordering guarantee
    // ("enqueue order IS freshness order") rests on payloads being immutable from enqueue on, and
    // the payload builders hand over nested objects BY LIVE REFERENCE (the grid's `getModel` load
    // shape carries cardData/expansion/brand/…/holoRow straight off the row) — an in-place
    // mutation between enqueue and the fire-time serialization would otherwise persist state the
    // enqueue-time gates never approved. Owned here rather than by each caller, so the invariant
    // cannot be reopened by the next payload builder that forgets.
    const snapshot = _.cloneDeep(loads);
    // `defer`: the request is BUILT when the chain reaches it, not when it is enqueued — building
    // it eagerly starts nothing on a cold HttpClient observable, but it makes "was the write
    // issued yet" unobservable to a spec (and to any interceptor-level bookkeeping).
    const outcome = this.chainDraftWrite(slot, defer(() => this.setInsertService.saveObjectKey(this.tabKeyFor(feature), snapshot, false)), 'autosave');
    outcome.subscribe(ok => {
      if (!ok) { return; }
      const latched = this.settleInFlight.get(slot);
      if (latched && this.settledFailed.has(latched)) {
        this.settleInFlight.delete(slot);
        this.settledFailed.delete(latched);
      }
    });
    return outcome;
  }

  saveDraft(loads: Loads): Observable<boolean> {
    return this.saveDraftForFeature(SET_INSERT_FEATURE, loads);
  }

  /** A draft DELETE for which "the key is not there" is the outcome asked for, not a failure.
   *
   *  A 404 means the draft is already gone, which is exactly what every caller wanted, and both
   *  callers pay for reporting it as a failure — differently, which is why the tolerance belongs
   *  here rather than at one of them:
   *  - the picker's per-draft delete (`deleteDraftChained`) keeps the row alive with a delete that
   *    can never "succeed" again, so the button reads as dead;
   *  - the post-commit settle (`settleDelete`) LATCHES the feature's listing gate as failed, and
   *    a commit that lands before the first autosave tick has written anything (5 minutes in
   *    production, and autosave is gated while a commit is in flight) deletes a key that was never
   *    there. From then until a cleanly-landed autosave heals the latch, every `listPendingDrafts`
   *    for that feature hides this tab's own draft and can raise `ownDraftUnavailable` — a warning
   *    about lost work, for a delete that was a no-op.
   *
   *  `key` is taken as a THUNK so the request stays built inside the `defer` the chain fires: the
   *  settle resolves it from `tabKeyFor(feature)` and must read the id at fire time, not enqueue
   *  time. Any other error still propagates and fails the write honestly. */
  private deleteToleratingMissing(key: () => string): Observable<unknown> {
    return defer(() => this.setInsertService.deleteLoads(key()).pipe(
      catchError(err => ((err as { status?: number })?.status === 404 ? of(null) : throwError(() => err))),
    ));
  }

  /** The picker's per-draft delete, riding the write chain of the FEATURE the key belongs to
   *  (any tab's key — the slot is resolved from the key's own prefix). A raw pass-through delete
   *  raced the chain: a queued autosave/settle for the same key fires whether or not anyone is
   *  subscribed, so it could land AFTER an unchained delete and re-create, on the backend, the
   *  very draft the user just deleted — offered again by the next mount's picker. Chained, the
   *  delete fires after every write enqueued before it, so deleted stays deleted. Emits whether
   *  the delete landed with the chain intact (`chainDraftWrite`); the caller keeps the row
   *  visible on `false`, since the draft may well still exist. */
  deleteDraftChained(key: string): Observable<boolean> {
    const feature = featureForKey(key);
    const slot = feature?.prefix ?? key;
    // Whether this delete is aimed at THIS tab's own draft. Deleting our own key really does change
    // what the backend holds for it — a latched settle-rewrite retried afterwards would re-create
    // the draft the user just deleted — so it counts. Another tab's key changes nothing about ours
    // and must not, since the whole slot shares one chain (see `slotWriteLanded`).
    const targetsOwnKey = feature != null && key === this.tabKeyFor(feature);
    // 'settle-delete': a queued user-initiated delete must never have its request swapped out by
    // a later write's payload, and must never swap out anyone else's. A 404 counts as SUCCESS —
    // see `deleteToleratingMissing`.
    const landed = this.chainDraftWrite(slot, this.deleteToleratingMissing(() => key), 'settle-delete', targetsOwnKey);
    // Registered so `listPendingDrafts` waits it out (see `pendingDeletes`); dropped on
    // completion, guarded so a later delete of the same key is not unregistered by this one.
    this.pendingDeletes.set(key, landed);
    landed.pipe(take(1)).subscribe(() => {
      if (this.pendingDeletes.get(key) === landed) { this.pendingDeletes.delete(key); }
    });
    return landed;
  }

  /**
   * Chains one backend draft WRITE — a post-commit settle, an autosave, or a picker delete —
   * behind whatever write of the same feature is still in flight, and reports whether it LANDED
   * CLEANLY: its own request succeeded AND the prior write completed rather than hanging. A prior
   * that COMPLETED — with either outcome — does not clamp: nothing of it is still in flight.
   *
   * The chain OWNS the subscription (the request fires as soon as the prior completes, caller or
   * no caller): a settle's caller is being destroyed by the post-commit navigation, and an
   * autosave whose `switchMap` moved on must still deliver the snapshot it took.
   *
   * Every write's OWN request is bounded by `SETTLE_LIST_TIMEOUT_MS`, armed when the request
   * STARTS: past the deadline the write is torn down (the `timeout` operator unsubscribes the
   * hung XHR) and resolves `hung`. Self-abort, never successor-abort — a successor's clock,
   * armed at its own enqueue, measured queue-wait rather than the prior's request time, so under
   * one slow head-of-line write every queued successor serially aborted a healthy prior whose own
   * request was seconds old. The abort matters either way: letting the zombie run meant a
   * hung-but-eventually-successful older write could land on the backend AFTER a fresher one and
   * silently restore its older snapshot. It is best-effort (the server may have received the
   * request in full before the socket dropped), which is why a hung write TAINTS its slot: every
   * write completing in the same unbroken chain clamps its outcome to failure even when its own
   * request succeeds, because the zombie can be applied server-side after ANY of them — clamping
   * only the immediate successor (the old rule) let the write two positions back report `landed`
   * with the hazard still standing. The taint clears when the chain drains; a write enqueued
   * onto an idle slot starts clean (see `taintedSlots`). Because every write self-bounds, every
   * outcome is guaranteed to emit — a waiter needs no clock of its own, and the wait behind a
   * backlog is the sum of the priors' own bounded requests, not one shared deadline.
   *
   * A write enqueued while the slot's tail is a NOT-YET-STARTED write of the SAME coalescible
   * kind supersedes it (`ChainedDraftWrite.supersede`) instead of queueing: an autosave behind a
   * queued autosave, a settle rewrite behind a queued settle rewrite — both full-grid snapshots
   * aimed at the same key, so only the newest is worth sending. The superseded caller (and any
   * listing gate built on it) shares the surviving write's outcome — its snapshot is delivered,
   * in the only sense that matters, by the fresher payload that replaced it. Deletes never
   * coalesce in either direction (`ChainWriteKind`).
   *
   * `targetsOwnKey` says whether this write is aimed at THIS TAB's draft key for the slot, and is
   * the only thing `slotWriteLanded` counts — see that field.
   */
  private chainDraftWrite(slot: string, request: Observable<unknown>, kind: ChainWriteKind, targetsOwnKey = true): Observable<boolean> {
    const prior = this.draftWriteTail.get(slot);
    if (kind !== 'settle-delete' && prior?.kind === kind && prior.supersede(request)) {
      return prior.landed;
    }
    const outcome = new ReplaySubject<ChainOutcome>(1);
    let pendingRequest = request;
    let started = false;
    let done = false;
    const finish = (o: ChainOutcome): void => {
      if (done) { return; }
      done = true;
      // Taint BEFORE emitting: the emission synchronously releases the queued successor, and its
      // own completion must already see the slot tainted.
      if (o === 'hung') { this.taintedSlots.add(slot); }
      outcome.next(o);
      outcome.complete();
      if (this.draftWriteTail.get(slot) === entry) {
        this.draftWriteTail.delete(slot);
        // The chain has drained: whatever hung in it is as resolved as it will ever observably
        // be, and the next write enqueued onto the now-idle slot starts clean — the same reach
        // the old single-link clamp gave a lone hung write.
        this.taintedSlots.delete(slot);
      }
    };
    const entry: ChainedDraftWrite = {
      outcome: outcome.asObservable(),
      kind,
      supersede: (fresher: Observable<unknown>): boolean => {
        if (started || done) { return false; }
        pendingRequest = fresher;
        return true;
      },
      landed: outcome.pipe(map(o => o === 'ok')),
    };
    this.draftWriteTail.set(slot, entry);
    // Every prior outcome is guaranteed to emit (each write self-bounds below), so this wait
    // needs no deadline of its own.
    (prior?.outcome ?? of('ok' as ChainOutcome)).pipe(take(1)).subscribe(() => {
      started = true;
      pendingRequest.pipe(
        map(() => 'ok' as ChainOutcome),
        catchError(() => of('failed' as ChainOutcome)),
        // An emission is part of the contract — the chain and `listPendingDrafts` take one — and
        // a stubbed or empty request (EMPTY in specs) completes without one.
        defaultIfEmpty('ok' as ChainOutcome),
        // The write's own deadline, armed HERE — at request start. On firing, `timeout`
        // unsubscribes the hung XHR (the self-abort) and resolves `hung`.
        timeout({ first: SETTLE_LIST_TIMEOUT_MS, with: () => of('hung' as ChainOutcome) }),
        take(1),
      ).subscribe(own => {
        // Recorded on the OWN outcome, before the taint clamp below: a write whose request
        // succeeded has put its payload on the backend whatever the chain then reports, and
        // `retryLatchedSettle` needs that fact — not the clamped verdict — to know its
        // commit-time snapshot has been superseded.
        if (own === 'ok' && targetsOwnKey) { this.slotWriteLanded.set(slot, (this.slotWriteLanded.get(slot) ?? 0) + 1); }
        // The slot taint, not `priorOutcome`: a hung write anywhere in this unbroken chain — not
        // only immediately ahead — can still be applied server-side after this one.
        //
        // Scoped to writes aimed at THIS TAB's key, because that is the only thing a zombie can
        // re-create. Every write that WRITES a draft — the autosave and the settle rewrite —
        // targets `tabKeyFor(feature)`, so a hung one can only ever restore this tab's own key;
        // the picker's delete of ANOTHER tab's key rides the same slot chain (one chain per
        // feature) and is untouched by it. Clamped blanket, an operator with a hung autosave of
        // their own deleted a sibling tab's draft, got a 200 (or a tolerated 404), and was told
        // `dialog.loadSetInsert.deleteFailed` with the row kept — a sticky warning about a delete
        // that had landed, and no way to make it succeed while the taint stood.
        finish(own === 'ok' && targetsOwnKey && this.taintedSlots.has(slot) ? 'failed' : own);
      });
    });
    return entry.landed;
  }

  /**
   * Runs — and OWNS the subscription of — the post-commit draft rewrite/delete, remembering it
   * while it is in flight so `listPendingDrafts` can wait it out.
   *
   * The commit's success handler fires this and (on a first commit) navigates; the navigation
   * mounts SetInsertConfComponent, whose `listPendingDrafts()` GET raced this very request on the
   * backend. Losing the race listed the draft as it was BEFORE the commit — every committed row
   * still filled — and restoring that repaints rows the commit already carried, so the next Save
   * POSTs the same warehouse movements a second time.
   *
   * The subscription lives here, not on the caller's SubSink, on purpose: the first commit's
   * navigation destroys the grid component, and an unsubscribe there would abort the request
   * mid-flight — leaving the backend holding a draft that describes committed rows. A failed
   * settle resolves (to `false`) rather than erroring so it can never wedge future pickers — and
   * the outcome is CARRIED, not discarded: `listPendingDrafts` hides THIS TAB'S OWN draft from a
   * listing it gated on a settle that reported failure, because the backend then still holds the
   * pre-commit draft under this tab's key, whose restore re-commits already-committed rows.
   *
   * Draft writes are SERIALIZED per feature (`chainDraftWrite`): a new settle chains onto
   * whatever write — settle OR autosave — is still in flight, and only fires its own request once
   * that completes. Two per-row saves in quick succession otherwise race their draft rewrites on
   * the backend — commit A's rewrite (draft still containing row Y) can land AFTER commit B's
   * (payload without Y), leaving the backend draft listing a row that was already committed,
   * while `listPendingDrafts` only ever waited out the LATEST settle. Autosaves ride the same
   * chain because a settle delayed behind a slow prior could land AFTER a fresher autosave and
   * overwrite its payload with the older commit-time snapshot. Chained through the replayed
   * outcome, so the prior request is never re-fired; every write's own request is bounded
   * (self-aborted past its deadline) and a hung prior clamps the next write's outcome — see
   * `chainDraftWrite`.
   *
   * The slot is cleared only when the settle reports SUCCESS. A failed settle stays latched,
   * replaying `false` to every later `listPendingDrafts` — a rewrite that never landed leaves the
   * stale pre-commit draft on the backend, and that draft is exactly as dangerous one navigation
   * later as it is during the request. The latch heals when a later settle succeeds (it replaces
   * and then clears the slot), when a chained autosave lands the full current state
   * (`saveDraftForFeature`), or when the next listing re-runs the settle and THAT lands
   * (`retryLatchedSettle`, which is why the request is kept). It is in-memory, so a reload starts
   * clean — by design, since nothing durable could tell a recovered backend from a stale one.
   */
  settleAfterCommit(request: Observable<unknown>, feature: DraftFeature = SET_INSERT_FEATURE,
                    kind: Exclude<ChainWriteKind, 'autosave'> = 'settle-delete'): void {
    this.enqueueSettle(request, feature, kind, true);
  }

  /** `settleAfterCommit`'s body. `retryable` is false for the retry itself, so a latch costs at
   *  most ONE extra round-trip: a backend wedged badly enough to hang the settle would otherwise
   *  hang its retry too, and every later mount would pay a fresh deadline for a request already
   *  known to be going nowhere — the per-mount stall the gate's own latch exists to prevent. */
  private enqueueSettle(request: Observable<unknown>, feature: DraftFeature,
                        kind: Exclude<ChainWriteKind, 'autosave'>, retryable: boolean): void {
    const slot = feature.prefix;
    // A settle REPLACING a latched one retires that latch: its own outcome is what the listing
    // will read from here on, and leaving the old gate marked failed would let a later autosave
    // "heal" a latch the slot no longer holds.
    const previous = this.settleInFlight.get(slot);
    if (previous) { this.settledFailed.delete(previous); }
    this.latchedSettleRequest.delete(slot);
    const settled = this.chainDraftWrite(slot, request, kind);
    // The listing GATE is the settle's outcome — and NOTHING ELSE: no clock of its own. Every
    // chained write self-bounds at request start (`chainDraftWrite`), so `settled` is guaranteed
    // to emit within the sum of the queue's own bounded requests: a hung settle latches through
    // its OWN deadline (outcome `hung` → `false`), never by a second clock here. The gate used
    // to carry one, armed at ENQUEUE — so a settle merely QUEUED behind a slow prior latched
    // `failed` while its rewrite went on to land, and the latch then hid this tab's draft (the
    // only copy of the remaining rows) from every picker until something else healed it. A
    // deadline that measures queue-wait punishes the write for the traffic ahead of it; the
    // per-request deadline already bounds everything this gate can wait on.
    const gate = settled.pipe(
      take(1),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.settleInFlight.set(slot, gate);
    gate.subscribe(ok => {
      if (this.settleInFlight.get(slot) !== gate) { return; }
      if (ok) {
        this.settleInFlight.delete(slot);
        this.latchedSettleRequest.delete(slot);
      } else {
        // Completed (or timed out) with failure: latched. Marked so a later successful autosave
        // can tell this latch apart from a settle still in flight, which it must leave alone, and
        // the request kept so the next listing can retry it once (`retryLatchedSettle`).
        this.settledFailed.add(gate);
        if (retryable) {
          this.latchedSettleRequest.set(slot, { request, kind, landedSeq: this.slotWriteLanded.get(slot) ?? 0 });
        }
      }
    });
  }

  /** Re-runs a feature's LATCHED settle, once, if it has one.
   *
   *  The latch itself is deliberately permanent — a rewrite that never landed leaves the pre-commit
   *  draft on the backend, whose restore re-commits already-committed rows, and that danger does
   *  not expire, so nothing about it may be relaxed on a timer. But its only two healers were a
   *  later commit and a successful chained autosave, and after a first commit the navigation has
   *  already destroyed the grid: the reloaded screen is empty, so it produces neither. That left
   *  this tab's draft — the sole copy of the rows the commit did not carry — hidden from every
   *  picker for the life of the page, with a `ownDraftUnavailable` warning naming work the operator
   *  had no way to reach. Retrying is the one move that CLEARS the danger rather than waiting it
   *  out: on success the backend no longer holds the pre-commit draft, the slot unlatches and the
   *  row is offered again; on failure the latch simply stands and nothing is offered that was not
   *  offered before.
   *
   *  Fired from `listPendingDrafts` AFTER that listing has captured the gate it will wait on, so
   *  the retry never adds to the wait: the mount that triggers it still answers instantly off the
   *  latch (`shareReplay`), and the healing shows up on the next one. Waiting for it instead would
   *  charge every mount a fresh deadline whenever the settle hangs rather than fails — exactly the
   *  per-mount stall the latch was introduced to end.
   *
   *  TWO freshness guards, because the retry re-subscribes the ORIGINAL request and a
   *  `settle-rewrite`'s `defer` closes over the COMMIT-TIME snapshot. Re-firing it is only ever
   *  safe while that snapshot is still the newest thing anyone has aimed at the key:
   *
   *  - **A write aimed at THIS TAB's key has LANDED since the latch** (`slotWriteLanded` moved — it
   *    counts own-key writes only, which is what makes it readable as freshness here) ⇒ DROPPED for
   *    good. The backend already holds a payload newer than the commit-time one, so re-sending the
   *    old snapshot would not heal anything — it would overwrite the fresher draft with rows the
   *    commit already carried, which is the very restore hazard the latch exists to prevent, caused
   *    rather than avoided. The latch itself STANDS (the taint that clamped that write to `failed`
   *    means a zombie may still land), it simply loses its retry. The autosave healer cannot cover
   *    this: it only fires on a `true` outcome, and a landed autosave clamped by `taintedSlots`
   *    reports `false`.
   *  - **The slot's chain is not empty** ⇒ SKIPPED for now, latch and request kept. A queued write
   *    is by construction fresher than the commit-time snapshot (enqueue order is freshness order),
   *    and the retry, being enqueued here, would land BEHIND it and overwrite it. If that write
   *    lands the first guard retires the retry on the next listing; if it fails, the retry is still
   *    valid and a later listing fires it. */
  private retryLatchedSettle(feature: DraftFeature): void {
    const slot = feature.prefix;
    const gate = this.settleInFlight.get(slot);
    const latched = this.latchedSettleRequest.get(slot);
    if (!gate || !latched || !this.settledFailed.has(gate)) {
      return;
    }
    if ((this.slotWriteLanded.get(slot) ?? 0) !== latched.landedSeq) {
      this.latchedSettleRequest.delete(slot);
      return;
    }
    if (this.draftWriteTail.has(slot)) {
      return;
    }
    this.enqueueSettle(latched.request, feature, latched.kind, false);
  }

  /** The post-commit draft REWRITE, built here so it rides the same write chain the autosaves
   *  use — a caller passing `saveDraft(...)`'s already-chained observable into
   *  `settleAfterCommit` would enqueue it twice. Snapshotted at enqueue for the same reason
   *  `saveDraftForFeature` snapshots (live references would serialize fire-time state), and
   *  passed as `settle-rewrite` so N rapid per-row saves coalesce: each save rebuilds the WHOLE
   *  remaining draft, so a queued, not-yet-started rewrite is fully described by the fresher one
   *  behind it — without coalescing, five quick per-row saves on a slow connection queued five
   *  serial dead-on-arrival full-draft PUTs of the same key. */
  settleRewrite(payload: Loads, feature: DraftFeature = SET_INSERT_FEATURE): void {
    const snapshot = _.cloneDeep(payload);
    this.settleAfterCommit(defer(() => this.setInsertService.saveObjectKey(this.tabKeyFor(feature), snapshot, false)), feature, 'settle-rewrite');
  }

  /** The post-commit draft DELETE of this tab's own key — see `settleRewrite` for why it is built
   *  here rather than handed in. Never coalesced (`settle-delete`), and a 404 counts as success
   *  (`deleteToleratingMissing`): a commit that lands before this tab has ever written a draft is
   *  deleting a key that was never there, and latching the feature's listing gate on that would
   *  hide the tab's own draft — and warn about work "that could not be saved" — over a no-op. */
  settleDelete(feature: DraftFeature = SET_INSERT_FEATURE): void {
    this.settleAfterCommit(this.deleteToleratingMissing(() => this.tabKeyFor(feature)), feature, 'settle-delete');
  }

  /**
   * Mark that THIS tab just committed a save (saveDefinitive success).
   * Consumed once by the next SetInsertConfComponent.ngOnInit, which then
   * suppresses the picker entirely on that mount - covering the post-commit
   * navigation from /set-insert-out to /set-insert-out/:id where offering
   * any picker (own draft OR other drafts) would surprise the user who just
   * acted decisively. Direct /:id visits (bookmark, deep link, header nav)
   * still show the picker normally because the flag is only set on commit.
   */
  markJustCommitted(): void {
    // Guarded: a refused write only costs the one-shot picker suppression (the picker shows once
    // where it would have been skipped), never the commit that just succeeded.
    writeStorage('session', JUST_COMMITTED_KEY, '1');
  }

  consumeJustCommitted(): boolean {
    const v = readStorage('session', JUST_COMMITTED_KEY) === '1';
    if (v) { removeStorage('session', JUST_COMMITTED_KEY); }
    return v;
  }

  listPendingDrafts(feature: DraftFeature = SET_INSERT_FEATURE): Observable<PendingDraft[]> {
    // Wait out an in-flight post-commit settle OF THIS FEATURE before asking the backend what
    // drafts exist: the GET otherwise races the draft rewrite/delete and can list — and offer to
    // restore — the PRE-commit draft, whose rows the commit already carried (see
    // `settleAfterCommit`). Another feature's settle says nothing about this feature's backend
    // draft, so it neither delays nor suppresses this listing.
    //
    // The wait is BOUNDED (a hung settle must not blind every later mount to every draft forever)
    // and the settle's OUTCOME decides what may be listed: on failure or timeout the backend's
    // state is stale-or-unknown for exactly ONE row — THIS TAB'S OWN draft, the key the settle
    // was rewriting — so that row is hidden (offering it re-commits its rows as duplicate
    // movements) while every other tab's draft, about which this tab's failure says nothing, is
    // listed normally. Suppressing the whole feature here sent the operator back to redo work
    // another tab's perfectly good draft still held — the same wrong-scope suppression the
    // per-feature slots fixed across features, recreated within one. A FAILED settle stays
    // latched in the slot (replaying instantly, so there is no wait) until a later settle
    // succeeds or a chained autosave heals it; the draft itself is never deleted here.
    //
    // No age filter: drafts never expire (the backend keeps them and the picker lists them
    // whatever their date — see the draft-plain-restore change on develop).
    //
    // A latched settle is RETRIED once, alongside (never ahead of) this listing — after a first
    // commit nothing else on the reloaded screen can heal it. See `retryLatchedSettle`.
    //
    // Still NO deadline of its own, and a whole-gate one was tried and rejected (review round 15).
    // The objection it answers is real — the awaited writes run SERIALLY, so a stalled backend with
    // a queued settle and two picker deletes holds the mounting screen's block overlay for the sum
    // of their deadlines — but a clock here cannot tell that queue apart from a HEALTHY one: a
    // settle whose own request takes 3s behind an autosave that takes 8s answers at 11s with every
    // individual request well inside its bound, and a 10s cap reports it FAILED, hiding the only
    // copy of the rows the commit did not carry. That is round 12's enqueue-anchored clock again,
    // one level up. Every outcome waited on here is already bounded per request; the honest fix for
    // the wait is fewer/faster writes, not a clock that guesses.
    //
    // "Bounded per request" is now true of the LISTING GET too (`LIST_DRAFTS_TIMEOUT_MS`), which
    // is the one request in this method that used to have no bound at all. That is the same
    // per-request rule the paragraph above endorses, not the whole-gate clock it rejects: it
    // measures one GET's own time on the wire, so it can never read a healthy queue as a wedged
    // backend.
    //
    // Pending picker DELETES of this feature are waited out alongside the settle: a queued
    // chained DELETE fires only when the chain reaches it, so a GET issued before then could
    // re-list — and offer to restore — the very draft the user just deleted. Once each delete's
    // outcome is in, the backend answers truthfully either way (landed → no longer listed,
    // failed → still exists and is listed).
    //
    // Everything below runs at SUBSCRIBE time, not at call time — hence the `defer`. Three things
    // in this body are effects or reads of live mutable state (the settle-gate capture, the
    // `retryLatchedSettle` enqueue, and the `pendingDeletes` snapshot), and without the wrapper a
    // caller that BUILDS the observable and subscribes to it later — or drops it on a `catchError`
    // path and never subscribes at all — enqueued a settle retry for a listing that never happens
    // and waited on a delete set already stale by the time the GET went out. Both in-tree callers
    // subscribe immediately, so this is a shape guarantee rather than a bug fix; it costs nothing
    // and removes the requirement that every future caller know to subscribe at once.
    return defer(() => {
      const settle = (this.settleInFlight.get(feature.prefix) ?? of(true)).pipe(take(1));
      // AFTER the gate above is captured, never before: this listing answers off the latch it
      // already holds, and the retry heals (or does not) for the next one. See `retryLatchedSettle`.
      this.retryLatchedSettle(feature);
      // A key whose prefix names no known feature is waited out by EVERY feature's listing, not by
      // none of it. `deleteDraftChained` gives such a key its own private chain slot (`?? key`), so
      // it is ordered against nothing; if this filter also drops it, both halves of the "deleted
      // stays deleted" guarantee are off at once and the GET can re-offer the draft the user just
      // deleted. The two fallbacks must not disagree, and the safe direction is to wait: the cost of
      // waiting out a delete that belonged to another feature is one bounded request, while the cost
      // of skipping it is re-offering a deleted draft.
      const deletes = [...this.pendingDeletes.entries()]
        .filter(([key]) => {
          const keyFeature = featureForKey(key);
          return keyFeature == null || keyFeature.prefix === feature.prefix;
        })
        .map(([, landed]) => landed.pipe(take(1)));
      const ownKey = this.tabKeyFor(feature);
      return forkJoin([settle, ...deletes]).pipe(
        switchMap(([settledOk]) => this.setInsertService.listDrafts(feature.prefix).pipe(
          // The listing's own bound — see LIST_DRAFTS_TIMEOUT_MS. Without it a wedged backend holds
          // the mounting screen's block overlay open indefinitely.
          timeout(LIST_DRAFTS_TIMEOUT_MS),
          map(res => {
            const rows = res.data || [];
            const suppressed = !settledOk && rows.some(r => r.key === ownKey);
            // Hiding this tab's own draft is the right call (see above) but it must not be SILENT.
            // After a first commit that navigated away, that draft is the only copy of the rows the
            // commit did NOT carry. The latch is retried on each listing (`retryLatchedSettle`), so
            // this is no longer necessarily final — but it is final for THIS listing, and a backend
            // that refused the settle will likely refuse the retry too. Announce it — the operator
            // can try again, instead of concluding the work is lost.
            if (suppressed) { this.ownDraftSuppressed.next(feature); }
            return rows
              .filter(r => settledOk || r.key !== ownKey)
              .map(r => this.toPendingDraft(r));
          })),
        ),
      );
    });
  }

  private toPendingDraft(row: DraftSummaryResponse): PendingDraft {
    const items = (row.value && (row.value.items as any[])) || [];
    return {
      key: row.key,
      savedAt: row.savedAt,
      itemCount: Array.isArray(items) ? items.length : 0,
      brand: (row.value?.filter?.customData as any)?.brand?.name,
      value: row.value,
    };
  }
}
